import { useState, useCallback } from "react";
import { ApiDataResponse, ApiResponse, Term } from "../../services/Dtos";
import { ApiService } from "../../services/ApiService";
import useAxios from "../useAxios";
import { useSchoolsContext } from "../../Contexts/schoolContext";
import { useApiResponseHandler } from "../useApiResponseHandler";

export const useTermService = () => {
  const { setTerms, setSchool } = useSchoolsContext();
  const axiosClient = useAxios();
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const { handleApiError, handleApiSuccess, clearErrors } =
    useApiResponseHandler({
      setErrors,
    });

  const createTerm = useCallback(
    async (
      term: Term,
      onSuccess?: (term: Term) => void
    ): Promise<{ result: ApiDataResponse<Term> }> => {
      setIsLoading(true);
      clearErrors();

      try {
        const result = await new ApiService<Term>("/terms", axiosClient).create(
          term
        );
        if (result.succeeded) {
          setTerms((prev: Term[]) =>
            [...prev, result.data].sort((a, b) => b.order - a.order)
          );
          handleApiSuccess("Term created successfully");
          onSuccess?.(term);
          return { result };
        } else {
          handleApiError(result, "Failed to create term");
          return { result };
        }
      } catch (error: any) {
        const errorMessage = handleApiError(error, "Failed to create term");
        return {
          result: {
            succeeded: false,
            message: errorMessage?.message || "Failed to create term",
            statusCode: 500,
            errors: error.response?.data.errors || [],
            data: {} as Term
          },
        };
      } finally {
        setIsLoading(false);
      }
    },
    [axiosClient, setTerms, handleApiError, handleApiSuccess, clearErrors]
  );

  const updateTerm = useCallback(
    async (
      id: string,
      term: Term,
      onSuccess?: (term: Term) => void
    ): Promise<{ result: ApiResponse }> => {
      setIsLoading(true);
      clearErrors();

      try {
        const result = await new ApiService<Term>("/terms", axiosClient).update(
          id,
          term
        );
        if (result.succeeded) {
          setTerms((prev: Term[]) => prev.map((s) => (s.id === id ? term : s)));
          handleApiSuccess("Term updated successfully");
          onSuccess?.(term);
          return { result };
        } else {
          handleApiError(result, "Failed to update term");
          return { result };
        }
      } catch (error: any) {
        const errorMessage = handleApiError(error, "Failed to update term");
        return {
          result: {
            succeeded: false,
            message: errorMessage?.message || "Failed to update term",
            statusCode: 500,
            errors: error.response?.data.errors || [],
          },
        };
      } finally {
        setIsLoading(false);
      }
    },
    [axiosClient, setTerms, handleApiError, handleApiSuccess, clearErrors]
  );

  const deleteTerm = useCallback(
    async (
      id: string,
      onSuccess?: (id: string) => void
    ): Promise<{ result: ApiResponse }> => {
      setIsLoading(true);
      clearErrors();

      try {
        const result = await new ApiService<Term>("/terms", axiosClient).delete(
          id
        );
        if (result.succeeded) {
          setTerms((prev: Term[]) => prev.filter((s) => s.id !== id));
          setSchool((prev: any) => {
            if (!prev) return prev;
            if (prev.currentTermId === id) {
              return { ...prev, currentTermId: "" };
            }
            return prev;
          });
          handleApiSuccess("Term deleted successfully");
          onSuccess?.(id);
          return { result };
        } else {
          handleApiError(result, "Failed to delete term");
          return { result };
        }
      } catch (error: any) {
        const errorMessage = handleApiError(error, "Failed to delete term");
        return {
          result: {
            succeeded: false,
            message: errorMessage?.message || "Failed to delete term",
            statusCode: 500,
            errors: error.response?.data.errors || [],
          },
        };
      } finally {
        setIsLoading(false);
        setOpenDeleteDialog(false);
      }
    },
    [
      axiosClient,
      setTerms,
      setSchool,
      handleApiError,
      handleApiSuccess,
      clearErrors,
    ]
  );

  return {
    createTerm,
    updateTerm,
    deleteTerm,
    isLoading,
    errors,
    clearErrors,
    openDeleteDialog,
    setOpenDeleteDialog,
  };
};
